/**
 * FASE 5 / BLOCO BA-2 — BI item 12 (edital Timbé do Sul):
 * "Possuir painéis e gráficos interativos para análise de dados e informações para
 *  auxílio nas tomadas de decisão."
 *
 * Este módulo é a LÓGICA da interatividade, deliberadamente extraída dos componentes Vue.
 *
 * Por que existe separado: o `web/` não tinha nenhuma infraestrutura de teste, e a
 * interatividade (filtro cruzado, drill-down, propagação entre widgets) é justamente a
 * parte do BI 12 que mais fácil se afirma e mais difícil se prova. Dentro de um componente
 * Vue, provar isso exigiria montar DOM; aqui é uma função pura, e função pura se testa.
 *
 * A renderização (cores, legendas, animação) continua nos componentes e é verificada por
 * checklist visual — declarado assim no critério de encerramento, sem chamar inspeção
 * visual de teste automatizado.
 *
 * REGRA: todas as funções são PURAS e IMUTÁVEIS. Nunca mutam o estado recebido; devolvem
 * um novo. Estado mutado em painel com vários widgets produz o bug clássico de "um widget
 * atualizou e o outro não", que é impossível de reproduzir depois.
 */

/** Estado inicial de um painel. */
function estadoInicial (painel) {
  return {
    slug: painel && painel.slug ? painel.slug : null,
    filtros: {},
    nivel: 0,
    caminho: [],
    widgetOrigem: null
  }
}

function clonar (estado) {
  return {
    slug: estado.slug,
    filtros: Object.assign({}, estado.filtros),
    nivel: estado.nivel,
    caminho: estado.caminho.slice(),
    widgetOrigem: estado.widgetOrigem
  }
}

/**
 * Filtro cruzado: clicar num valor de um widget filtra os DEMAIS widgets.
 *
 * Três regras que o teste verifica:
 *  - clicar de novo no MESMO valor remove o filtro (alterna);
 *  - clicar em OUTRO valor da mesma dimensão substitui;
 *  - filtros de dimensões diferentes ACUMULAM.
 */
function aplicarFiltroCruzado (estado, widget, valor) {
  const novo = clonar(estado)
  const dimensao = widget && widget.dimensao ? widget.dimensao : null
  if (!dimensao) return novo

  const chave = valor === null || valor === undefined ? '' : String(valor)

  if (novo.filtros[dimensao] === chave) {
    delete novo.filtros[dimensao]
    novo.widgetOrigem = null
  } else {
    novo.filtros[dimensao] = chave
    novo.widgetOrigem = widget.id || null
  }

  return novo
}

function removerFiltro (estado, dimensao) {
  const novo = clonar(estado)
  delete novo.filtros[dimensao]
  if (Object.keys(novo.filtros).length === 0) novo.widgetOrigem = null
  return novo
}

/** Limpa filtros e drill-down, preservando a identidade do painel. */
function limpar (estado) {
  const novo = clonar(estado)
  novo.filtros = {}
  novo.nivel = 0
  novo.caminho = []
  novo.widgetOrigem = null
  return novo
}

/**
 * Drill-down: desce um nível fixando o valor daquela dimensão.
 * O caminho preserva o que foi fixado, para que `subir()` restaure exatamente o estado
 * anterior — inclusive um filtro que já existia antes da descida.
 */
function descer (estado, dimensao, valor) {
  const novo = clonar(estado)
  if (!dimensao) return novo

  const chave = valor === null || valor === undefined ? '' : String(valor)

  novo.caminho.push({
    dimensao: dimensao,
    valor: chave,
    // O que havia nesta dimensão antes de descer. `undefined` = não havia filtro.
    anterior: Object.prototype.hasOwnProperty.call(estado.filtros, dimensao)
      ? estado.filtros[dimensao]
      : undefined
  })

  novo.filtros[dimensao] = chave
  novo.nivel = novo.caminho.length

  return novo
}

/** Sobe um nível, restaurando o filtro que existia antes daquela descida. */
function subir (estado) {
  const novo = clonar(estado)
  if (novo.caminho.length === 0) return novo   // no nível 0 é no-op

  const passo = novo.caminho.pop()

  if (passo.anterior === undefined) {
    delete novo.filtros[passo.dimensao]
  } else {
    novo.filtros[passo.dimensao] = passo.anterior
  }

  novo.nivel = novo.caminho.length
  return novo
}

/**
 * Quais widgets precisam recarregar depois de uma mudança de estado.
 *
 * Sem isto o painel recarrega tudo a cada clique (lento) ou recarrega de menos (widget
 * desatualizado na tela, que é pior — o gestor lê número errado).
 *
 * Um widget é afetado quando consome alguma das dimensões filtradas. O widget de ORIGEM
 * do filtro não recarrega: ele já mostra a seleção, e recarregá-lo o reduziria à própria
 * barra clicada.
 */
function widgetsAfetados (estado, painel) {
  const widgets = (painel && painel.widgets) || []
  const dimensoesFiltradas = Object.keys(estado.filtros)

  if (dimensoesFiltradas.length === 0) {
    return widgets.map(function (w) { return w.id })
  }

  return widgets
    .filter(function (w) {
      if (w.id === estado.widgetOrigem) return false
      const consome = w.consome || (w.dimensao ? [w.dimensao] : [])
      return dimensoesFiltradas.some(function (d) { return consome.indexOf(d) !== -1 })
    })
    .map(function (w) { return w.id })
}

/** Payload enviado à API para um widget, já com filtros e nível aplicados. */
function parametrosDeConsulta (estado, widget) {
  const params = Object.assign({}, estado.filtros)

  if (widget && widget.dimensao) {
    // O widget não se auto-filtra: senão devolveria uma única barra, a que foi clicada.
    delete params[widget.dimensao]
    params.dimensao = widget.dimensao
  }

  params.nivel = estado.nivel
  return params
}

/** Rótulo da trilha de navegação (breadcrumb) do drill-down. */
function trilha (estado) {
  return estado.caminho.map(function (p) {
    return { dimensao: p.dimensao, valor: p.valor }
  })
}

export {
  estadoInicial,
  aplicarFiltroCruzado,
  removerFiltro,
  limpar,
  descer,
  subir,
  widgetsAfetados,
  parametrosDeConsulta,
  trilha
}
